import { useMemo, useState } from 'react';
import { getNavigationDefinition, type NavigationSection, type ResolvedNavigationSection } from './navigation';

export interface SearchableNavItem {
  href: string;
  label: string;
  description?: string;
  section: string;
  group: string;
  context: string;
  keywords: string[];
}

export function buildSearchIndex(nav: readonly (NavigationSection | ResolvedNavigationSection)[] = getNavigationDefinition()): SearchableNavItem[] {
  const items: SearchableNavItem[] = [];
  for (const section of nav) {
    for (const group of section.groups) {
      for (const item of group.items) {
        if (!item.href || item.comingSoon || ('isDisabled' in item && item.isDisabled)) continue;
        items.push({
          href: item.href,
          label: item.label,
          description: item.description,
          section: section.label,
          group: group.label,
          context: section.context,
          keywords: [
            section.label.toLowerCase(),
            group.label.toLowerCase(),
            item.label.toLowerCase(),
            (item.description ?? '').toLowerCase(),
          ],
        });
      }
    }
  }
  return items;
}

export interface ScoredItem extends SearchableNavItem {
  score: number;
}

export function searchNavigation(
  index: SearchableNavItem[],
  query: string,
  limit = 12,
): ScoredItem[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const scored: ScoredItem[] = [];
  for (const item of index) {
    let score = 0;
    if (item.label.toLowerCase().startsWith(q)) score += 100;
    else if (item.label.toLowerCase().includes(q)) score += 50;
    if (item.keywords.some((kw) => kw.includes(q))) score += 20;
    if (item.description?.toLowerCase().includes(q)) score += 10;
    if (item.section.toLowerCase().includes(q)) score += 15;
    if (item.group.toLowerCase().includes(q)) score += 15;
    if (score > 0) scored.push({ ...item, score });
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, limit);
}

export interface HighlightSegments {
  text: string;
  match: boolean;
}

export function highlightMatches(text: string, query: string): HighlightSegments[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [{ text, match: false }];
  const lower = text.toLowerCase();
  const segments: HighlightSegments[] = [];
  let i = 0;
  while (i < text.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) {
      segments.push({ text: text.slice(i), match: false });
      break;
    }
    if (idx > i) segments.push({ text: text.slice(i, idx), match: false });
    segments.push({ text: text.slice(idx, idx + q.length), match: true });
    i = idx + q.length;
  }
  return segments;
}

export function useSidebarSearch(sections?: readonly ResolvedNavigationSection[]) {
  const [query, setQuery] = useState('');
  const index = useMemo(() => buildSearchIndex(sections), [sections]);
  const results = useMemo(() => searchNavigation(index, query), [index, query]);
  return { query, setQuery, results, index };
}
